import SectionNav from "@/components/SectionNav";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Compass, Mountain, Sparkles, MapPin, Calendar,
  Phone, MessageCircle, Mail, Building2, ChevronDown, BookOpen, Crown, Users,
  Globe2, Tag, Filter, Clock, Plane, Send, Leaf, Heart,
} from "lucide-react";
import { useLanguage, pick } from "@/contexts/LanguageContext";
import { useFavorites } from "@/contexts/FavoritesContext";
import { pathFor } from "@/lib/routes";
import { CONTACT } from "@/lib/data";
import { REGIONS, EXPERIENCES, TRIPS } from "@/lib/tripsData";
import { IMG } from "@/lib/imageBank";
import ContactForm from "@/components/ContactForm";
import HomeAllTripsCatalog from "@/components/HomeAllTripsCatalog";
import EditableImage from "@/components/EditableImage";
import FromPrice from "@/components/FromPrice";
import PexelsMoroccoVideos from "@/components/PexelsMoroccoVideos";
import ToursRegionMap from "@/components/ToursRegionMap";
import XalucaLogoBadge from "@/components/XalucaLogoBadge";
import ImageContactBubble from "@/components/ImageContactBubble";
import IdealTripWizard from "@/components/IdealTripWizard";
import { requestWhatsAppContact } from "@/components/WhatsAppContactModal";
import { WHATSAPP_URL } from "@/components/WhatsAppIcon";
import { SOUTH_TRIPS, NORTH_TRIPS, FULL_TRIPS } from "@/lib/homeCarousels";
import ExpandableTripCard from "@/components/ExpandableTripCard";
import FeaturedDepartureBanner from "@/components/FeaturedDepartureBanner";

const ICONS = { Sparkles, BookOpen, Mountain, Crown, Users, Leaf };

// Region card "from" price = the lowest real tariff among that region's trips,
// so it stays consistent with the trip pages and /precios.
const REGION_TRIPS = { south: SOUTH_TRIPS, north: NORTH_TRIPS, all: FULL_TRIPS };
const regionRouteIds = (regionId) =>
  (REGION_TRIPS[regionId] || []).map((t) => t.routeId).filter(Boolean);

/* ============================================================
   1 — Hero
============================================================ */
const Hero = ({ t }) => (
  <section
    data-testid="viajes-hero"
    className="relative min-h-[100svh] w-full overflow-hidden bg-[#1A1513]"
  >
    <EditableImage
      slot="viajes.hero.bg"
      fallback="https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=2400&q=85"
      alt=""
      priority
      aspectRatio="16/9"
      className="ken-burns absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/95 via-[#1A1513]/55 to-[#1A1513]/35 pointer-events-none" />
    <div className="absolute inset-0 berber-bg-cross opacity-40" aria-hidden="true" />
    <span className="film-grain" />

    <div className="relative z-10 min-h-[100svh] flex flex-col">
      <div className="flex-1 flex items-end pt-32 md:pt-44 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="max-w-3xl">
            <div className="fade-up inline-flex items-center gap-3 text-[#D4A373]">
              <Compass className="w-3.5 h-3.5" strokeWidth={1.6} />
              <span className="text-[11px] tracking-[0.35em] uppercase font-semibold">{t.eyebrow}</span>
              <span className="w-8 h-px bg-[#D4A373]/50" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4A373]/80">{t.eyebrow_place}</span>
            </div>
            <h1 className="fade-up fade-up-delay-1 font-serif-x text-[#FDFBF7] text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight mt-6">
              {t.title}
            </h1>
            <p className="fade-up fade-up-delay-2 mt-8 max-w-2xl text-base md:text-lg text-[#FDFBF7]/85 leading-relaxed">
              {t.subtitle}
            </p>
            <p className="fade-up fade-up-delay-3 mt-4 max-w-2xl text-sm md:text-base text-[#FDFBF7]/65 leading-relaxed">
              {t.intro}
            </p>
            <div className="fade-up fade-up-delay-4 mt-10 flex flex-wrap items-center gap-4">
              <a href="#regions" data-testid="viajes-hero-cta-primary"
                 className="xaluca-button inline-flex items-center gap-3 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-8 py-4 text-[11px] tracking-[0.25em] uppercase transition-colors">
                {t.cta_primary}
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
              </a>
              <a href="#explorer" data-testid="viajes-hero-cta-secondary"
                 className="xaluca-button inline-flex items-center gap-3 border border-[#FDFBF7]/40 hover:border-[#FDFBF7] hover:bg-[#FDFBF7] hover:text-[#1A1513] text-[#FDFBF7] px-7 py-4 text-[11px] tracking-[0.25em] uppercase transition-all duration-300">
                {t.cta_secondary}
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>
      </div>

      <a href="#intro" data-testid="viajes-scroll-indicator"
         className="absolute bottom-6 left-1/2 -translate-x-1/2 inline-flex flex-col items-center gap-2 text-[#FDFBF7]/65 hover:text-[#D4A373] transition-colors">
        <span className="text-[10px] tracking-[0.35em] uppercase">{t.scroll}</span>
        <ChevronDown className="w-4 h-4 animate-bounce" strokeWidth={1.5} />
      </a>
    </div>
  </section>
);

/* ============================================================
   2 — Editorial intro (brief)
============================================================ */
const EditorialIntro = ({ t, postcard }) => (
  <section id="intro" data-testid="viajes-intro"
           className="relative bg-[#FDFBF7] py-20 md:py-28 overflow-hidden">
    <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center">
      <span className="overline">{t.eyebrow}</span>
      <h2 className="font-serif-x text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-5 text-[#2C2621]">
        {t.title}
      </h2>
      <p className="mt-8 font-serif-x-italic text-xl md:text-2xl text-[#5C5248] leading-[1.5] max-w-3xl mx-auto">
        {t.body}
      </p>
    </div>

    {postcard && <TravelPostcard p={postcard} />}
  </section>
);

/* ----- Travel postcard — emotional editorial block ----- */
const TravelPostcard = ({ p }) => (
  <div className="relative mt-16 md:mt-20 max-w-4xl mx-auto px-3 sm:px-6" data-testid="viajes-postcard">
    {/* tape pieces */}
    <span className="postcard-tape absolute -top-3 left-10 w-20 h-7 rotate-[-6deg] z-20 hidden sm:block" aria-hidden="true" />
    <span className="postcard-tape absolute -top-3 right-12 w-16 h-7 rotate-[5deg] z-20 hidden sm:block" aria-hidden="true" />

    <article className="postcard-paper relative overflow-hidden border border-[#2C2621]/15 shadow-[0_30px_60px_-25px_rgba(26,21,19,0.45)] rotate-[-1.2deg] transition-transform duration-500 hover:rotate-0">
      <div className="absolute inset-0 berber-bg-diamond opacity-[0.05] pointer-events-none" aria-hidden="true" />

      {/* header strip */}
      <div className="relative flex flex-wrap items-center justify-between gap-2 px-6 md:px-10 pt-6">
        <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-[#A07042]">
          <Send className="w-3.5 h-3.5" strokeWidth={1.6} /> {p.label}
        </span>
        <span className="font-hand text-2xl md:text-3xl text-[#C16542] -rotate-2">{p.tagline}</span>
      </div>

      <div className="mx-6 md:mx-10 mt-4 border-t border-dashed border-[#2C2621]/20" />

      <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 px-6 md:px-10 py-8 md:py-10">
        {/* message side */}
        <div className="md:col-span-8 order-2 md:order-1">
          <p className="font-hand text-3xl md:text-4xl text-[#3A2E26] leading-tight mb-3" data-testid="postcard-greeting">
            {p.greeting}
          </p>
          <div className="space-y-4">
            {p.body.map((para, i) => (
              <p key={`pc-${i}`} className="font-hand text-[22px] md:text-[26px] leading-[1.5] text-[#2C2621]/90">
                {para}
              </p>
            ))}
          </div>
          <p className="font-hand text-2xl md:text-3xl text-[#5A6B4F] mt-6">{p.closing}</p>
          <p className="font-hand text-4xl md:text-5xl text-[#C16542] mt-1 -rotate-2" data-testid="postcard-signature">
            {p.signature}
          </p>
        </div>

        {/* stamp + postmark side */}
        <div className="md:col-span-4 order-1 md:order-2 md:border-l md:border-dashed md:border-[#2C2621]/20 md:pl-8 flex flex-col items-center md:items-end gap-7">
          <div className="relative">
            <div className="relative w-28 rotate-3 bg-[#FDFBF7] p-[5px] shadow-[0_3px_12px_rgba(26,21,19,0.28)]">
              <div className="relative overflow-hidden bg-[#1A1513]">
                <EditableImage
                  slot="viajes.postcard.stamp"
                  fallback={IMG.camelCaravan}
                  alt={p.stamp_caption}
                  aspectRatio="7/8"
                  imgProps={{ loading: "lazy" }}
                  className="block w-full"
                />
              </div>
              <span className="absolute inset-0 border border-dashed border-[#2C2621]/30 pointer-events-none" aria-hidden="true" />
            </div>
            {/* postmark overlapping the stamp */}
            <div className="postcard-postmark absolute -bottom-5 -left-7 w-24 h-24 rounded-full flex flex-col items-center justify-center text-center bg-[#F6EEDC]/40">
              <span className="text-[7px] tracking-[0.2em] uppercase leading-tight">Xaluca Tours</span>
              <Plane className="w-4 h-4 my-0.5" strokeWidth={1.4} />
              <span className="text-[10px] font-semibold tracking-[0.12em]">{p.postmark}</span>
            </div>
          </div>
          <p className="font-hand text-xl text-[#2C2621]/65 text-center md:text-right mt-2">{p.stamp_caption}</p>
        </div>
      </div>
    </article>
  </div>
);

/* ============================================================
   3 — Regions: 3 cinematic cards
============================================================ */
const RegionsSection = ({ t, lang }) => (
  <section id="regions" data-testid="viajes-regions"
           className="relative bg-[#FDFBF7] py-24 md:py-32 overflow-hidden">
    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14 md:mb-16">
        <div className="md:col-span-7">
          <span className="overline inline-flex items-center gap-2">
            <Globe2 className="w-3.5 h-3.5" strokeWidth={1.6} />
            {t.overline}
          </span>
          <h2 className="font-serif-x text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-5 text-[#2C2621]">
            {t.title}
          </h2>
        </div>
        <div className="md:col-span-5">
          <p className="text-base md:text-lg text-[#5C5248] leading-relaxed">{t.body}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {REGIONS.map((r) => (
          <article key={r.id} className="relative h-[60vh] min-h-[420px] max-h-[560px]">
            <Link to={pathFor(lang, r.routeId)} data-testid={`region-card-${r.id}`}
                  className="group relative block h-full overflow-hidden">
              <EditableImage
                slot={`viajes.region.${r.id}`}
                fallback={r.image}
                alt={pick(r.label, lang)}
                aspectRatio="3/4"
                imgProps={{ loading: "lazy" }}
                className="ken-burns absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/95 via-[#1A1513]/45 to-[#1A1513]/15 pointer-events-none" />
              <span className="film-grain" />
              <XalucaLogoBadge testid={`region-logo-${r.id}`} />

              <div className="absolute inset-0 p-7 md:p-9 flex flex-col justify-end text-[#FDFBF7]">
                <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase"
                      style={{ color: r.accent === "#3A4A5F" ? "#D4A373" : r.accent }}>
                  <span className="w-6 h-px" style={{ background: "currentColor" }} />
                  {pick(r.tagline, lang)}
                </span>
                <h3 className="font-serif-x text-3xl md:text-[34px] leading-[1.05] mt-3">
                  {pick(r.label, lang)}
                </h3>
                <p className="mt-4 text-sm text-[#FDFBF7]/85 leading-relaxed max-w-md">
                  {pick(r.body, lang)}
                </p>
                <div className="mt-4">
                  <FromPrice tone="light" size="sm" routeIds={regionRouteIds(r.id)} testid={`region-from-${r.id}`} />
                </div>
                <span className="mt-5 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#D4A373] group-hover:gap-4 transition-all duration-300">
                  {t.cta}
                  <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                </span>
              </div>
            </Link>
            <ImageContactBubble
              slug={`viajes-region-${r.id}`}
              align="left"
              vertical="top"
              zClass="z-[20]"
            />
          </article>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================
   4 — Experiences: 5 cards
============================================================ */
const ExperiencesSection = ({ t, lang }) => (
  <section id="experiences" data-testid="viajes-experiences"
           className="relative bg-[#1A1513] text-[#FDFBF7] py-24 md:py-32 overflow-hidden">
    <div className="absolute inset-0 berber-bg-cross opacity-50 pointer-events-none" aria-hidden="true" />
    <span className="film-grain" />

    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14 md:mb-16">
        <div className="md:col-span-7">
          <span className="overline text-[#D4A373] inline-flex items-center gap-2">
            <Tag className="w-3.5 h-3.5" strokeWidth={1.6} />
            {t.overline}
          </span>
          <h2 className="font-serif-x text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-5">
            {t.title}
          </h2>
        </div>
        <div className="md:col-span-5">
          <p className="text-base md:text-lg text-[#FDFBF7]/75 leading-relaxed">{t.body}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#FDFBF7]/10 border border-[#FDFBF7]/15">
        {EXPERIENCES.map((e) => {
          const Icon = ICONS[e.icon] || Sparkles;
          return (
            <article key={e.id} data-testid={`experience-card-${e.id}`}
                     className="group relative overflow-hidden bg-[#1A1513] hover:bg-[#221A16] transition-colors duration-500 flex flex-col">
              <div className="relative aspect-[5/3] overflow-hidden">
                <EditableImage
                  slot={`viajes.experience.${e.id}`}
                  fallback={e.image}
                  alt={pick(e.label, lang)}
                  aspectRatio="5/3"
                  imgProps={{ loading: "lazy" }}
                  className="absolute inset-0 w-full h-full object-cover opacity-70 transition-all duration-[1200ms] ease-out group-hover:opacity-100 group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513] via-[#1A1513]/40 to-transparent pointer-events-none" />
                <XalucaLogoBadge testid={`experience-logo-${e.id}`} />
                <ImageContactBubble
                  slug={`viajes-experience-${e.id}`}
                  align="left"
                  vertical="top"
                  zClass="z-[20]"
                />
                <span className="absolute bottom-4 left-4 inline-flex items-center justify-center w-12 h-12 rounded-full border bg-[#1A1513]/70 backdrop-blur-sm"
                      style={{ borderColor: `${e.accent}99`, color: e.accent }}>
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </span>
              </div>
              <div className="p-7 md:p-9 flex flex-col flex-1">
                <h3 className="font-serif-x text-2xl md:text-[26px] leading-[1.1]">
                  {pick(e.label, lang)}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-[#FDFBF7]/75 flex-1">
                  {pick(e.blurb, lang)}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 self-start">
                  <a href="#explorer"
                     data-testid={`experience-explore-${e.id}`}
                     className="xaluca-button inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase border-b pb-1 group-hover:gap-3 transition-all duration-300"
                     style={{ borderColor: `${e.accent}66`, color: e.accent }}>
                    {t.cta}
                    <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                  </a>
                  <a href={`tel:${CONTACT.phoneRaw}`}
                     data-testid={`experience-call-${e.id}`}
                     className="xaluca-button inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase border-b pb-1 group-hover:gap-3 transition-all duration-300"
                     style={{ borderColor: `${e.accent}66`, color: e.accent }}>
                    {t.cta_call}
                    <Phone className="w-3 h-3" strokeWidth={1.5} />
                  </a>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

/* ============================================================
   5 — Interactive Trip Explorer (filter by region + experience)
============================================================ */
const TripExplorer = ({ t, lang }) => {
  const { isFavorite, toggleFavorite } = useFavorites();
  const [region, setRegion] = useState("all-filter");
  const [experience, setExperience] = useState("all-filter");

  const filtered = useMemo(() => {
    return TRIPS.filter((trip) => {
      const matchesRegion = region === "all-filter" || trip.region === region;
      const matchesExp = experience === "all-filter" || trip.experiences.includes(experience);
      return matchesRegion && matchesExp;
    });
  }, [region, experience]);

  return (
    <section id="explorer" data-testid="viajes-explorer"
             className="relative bg-[#F2EBE1] py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 berber-bg-diamond opacity-40 pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12">
          <div className="md:col-span-7">
            <span className="overline inline-flex items-center gap-2">
              <Filter className="w-3.5 h-3.5" strokeWidth={1.6} />
              {t.overline}
            </span>
            <h2 className="font-serif-x text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-5 text-[#2C2621]">
              {t.title}
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-base md:text-lg text-[#5C5248] leading-relaxed">{t.body}</p>
          </div>
        </div>

        {/* Filter row — region */}
        <div className="mt-2">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248] mb-3">{t.label_region}</p>
          <div role="tablist" aria-label="Region filter" className="flex flex-wrap gap-2">
            <FilterChip data-testid="region-chip-all"
                        active={region === "all-filter"}
                        onClick={() => setRegion("all-filter")}>
              {t.all}
            </FilterChip>
            {REGIONS.map((r) => (
              <FilterChip key={r.id} data-testid={`region-chip-${r.id}`}
                          active={region === r.id} accent={r.accent}
                          onClick={() => setRegion(r.id)}>
                {pick(r.label, lang)}
              </FilterChip>
            ))}
          </div>
        </div>

        {/* Filter row — experience */}
        <div className="mt-7">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248] mb-3">{t.label_experience}</p>
          <div role="tablist" aria-label="Experience filter" className="flex flex-wrap gap-2">
            <FilterChip data-testid="experience-chip-all"
                        active={experience === "all-filter"}
                        onClick={() => setExperience("all-filter")}>
              {t.all}
            </FilterChip>
            {EXPERIENCES.map((e) => (
              <FilterChip key={e.id} data-testid={`experience-chip-${e.id}`}
                          active={experience === e.id} accent={e.accent}
                          onClick={() => setExperience(e.id)}>
                {pick(e.label, lang)}
              </FilterChip>
            ))}
          </div>
        </div>

        {/* Result count */}
        <div className="mt-8 flex items-center justify-between flex-wrap gap-3">
          <p data-testid="explorer-result-count"
             className="text-sm text-[#5C5248]">
            <span className="font-serif-x text-2xl text-[#2C2621] mr-2">{filtered.length}</span>
            {filtered.length === 1 ? t.result_singular : t.result_plural}
          </p>
          {(region !== "all-filter" || experience !== "all-filter") && (
            <button
              data-testid="explorer-clear"
              onClick={() => { setRegion("all-filter"); setExperience("all-filter"); }}
              className="text-[10px] tracking-[0.3em] uppercase text-[#C16542] hover:text-[#2C2621] transition-colors border-b border-[#C16542]/40 pb-1"
            >
              {t.clear}
            </button>
          )}
        </div>

        {/* Trip grid */}
        <div className="mt-8 grid grid-cols-1 items-start sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {filtered.length === 0 ? (
            <div data-testid="explorer-empty"
                 className="col-span-full bg-[#FDFBF7] border border-[#2C2621]/10 p-12 text-center">
              <p className="font-serif-x text-2xl text-[#2C2621]">{t.empty_title}</p>
              <p className="mt-3 text-sm text-[#5C5248]">{t.empty_body}</p>
            </div>
          ) : filtered.map((trip) => {
            const exps = trip.experiences.map((eid) => EXPERIENCES.find((e) => e.id === eid)).filter(Boolean);
            const reg = REGIONS.find((r) => r.id === trip.region);
            const fav = isFavorite(trip.id);
            return (
              <ExpandableTripCard key={trip.id} title={pick(trip.title, lang)} lang={lang}
                description={pick(trip.summary, lang)} destinations={exps.map((e) => pick(e.label, lang))}
                href={pathFor(lang, trip.routeId || "contact")} ctaLabel={t.cta_card}
                testIdPrefix={`trip-${trip.id}`} testIds={{ card: `trip-card-${trip.id}` }}>
                <div data-trip-card-image="" className="relative aspect-[4/3] overflow-hidden bg-[#1A1513]">
                  <EditableImage
                    slot={`viajes.trip.${trip.id}`}
                    fallback={trip.image}
                    alt={pick(trip.title, lang)}
                    aspectRatio="4/3"
                    imgProps={{ loading: "lazy" }}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
                  />
                  <span className="absolute top-3 left-3 inline-flex max-w-[calc(100%-4.5rem)] items-center gap-2 bg-[#FDFBF7]/95 backdrop-blur-sm px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase"
                        style={{ color: reg?.accent || "#5C5248" }}>
                    <MapPin className="w-3 h-3 shrink-0" strokeWidth={1.6} />
                    <span className="min-w-0 break-words">{pick(reg?.label, lang)}</span>
                  </span>
                  {/* Save to favourites — toggles without leaving the page */}
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(trip.id); }}
                    data-testid={`trip-fav-${trip.id}`}
                    aria-pressed={fav}
                    aria-label={fav ? t.fav_remove : t.fav}
                    title={fav ? t.fav_remove : t.fav}
                    className={`absolute top-3 right-3 z-10 inline-flex items-center justify-center w-9 h-9 rounded-full backdrop-blur shadow-md transition-colors ${
                      fav ? "bg-[#C16542] text-[#FDFBF7]" : "bg-[#FDFBF7]/90 text-[#C16542] hover:bg-[#C16542] hover:text-[#FDFBF7]"
                    }`}
                  >
                    <Heart className="w-4 h-4 transition-transform active:scale-90" strokeWidth={1.7} fill={fav ? "currentColor" : "none"} />
                  </button>
                  <XalucaLogoBadge
                    className="bottom-3 left-3 w-11 h-11"
                    testid={`trip-logo-${trip.id}`}
                  />
                  <span className="absolute bottom-3 right-3 inline-flex max-w-[calc(100%-5rem)] items-center gap-2 bg-[#1A1513]/65 backdrop-blur-sm text-[#FDFBF7] px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase">
                    <Clock className="w-3 h-3 shrink-0" strokeWidth={1.6} />
                    <span className="min-w-0 break-words">{pick(trip.duration, lang)}</span>
                  </span>
                </div>
                <div className="p-4 sm:p-5 xl:p-6 flex min-w-0 flex-col flex-1">
                  <h3 className="break-words font-serif-x text-xl md:text-[22px] leading-[1.15] text-[#2C2621]">
                    {pick(trip.title, lang)}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#5C5248] flex-1">
                    {pick(trip.summary, lang)}
                  </p>

                  {/* Experience badges */}
                  <div className="mt-5 flex flex-wrap gap-1.5">
                    {exps.map((e) => (
                      <span key={e.id} className="max-w-full break-words text-[9px] tracking-[0.2em] uppercase px-2 py-1 border"
                            style={{ borderColor: `${e.accent}55`, color: e.accent }}>
                        {pick(e.label, lang)}
                      </span>
                    ))}
                  </div>

                  <div className="mt-5 pt-4 border-t border-[#2C2621]/10 flex flex-wrap items-end justify-between gap-3">
                    <FromPrice routeId={trip.routeId} tone="dark" size="sm" className="flex-wrap" testid={`trip-from-${trip.id}`} />
                    <Link to={pathFor(lang, trip.routeId || "contact")}
                          data-testid={`trip-cta-${trip.id}`}
                          className="xaluca-button inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#C16542] hover:gap-3 transition-all duration-300 border-b border-[#C16542]/40 pb-1">
                      {t.cta_card}
                      <ArrowRight className="w-3 h-3 shrink-0" strokeWidth={1.5} />
                    </Link>
                  </div>
                </div>
              </ExpandableTripCard>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FilterChip = ({ active, accent, onClick, children, ...rest }) => (
  <button
    {...rest}
    onClick={onClick}
    role="tab"
    aria-selected={active}
    className={`px-4 md:px-5 py-2 text-[10px] tracking-[0.25em] uppercase transition-all duration-300 ${
      active
        ? "bg-[#2C2621] text-[#FDFBF7] border border-[#2C2621]"
        : "border border-[#2C2621]/20 text-[#5C5248] hover:text-[#2C2621]"
    }`}
    style={active && accent ? { background: accent, borderColor: accent } : undefined}
  >
    {children}
  </button>
);

/* ============================================================
   6 — Próximas Salidas (kept from previous design)
============================================================ */
const ProximasSalidas = ({ t, lang }) => {
  const DEPARTURES = [
    { season: { es: "Semana Santa 2026", en: "Easter 2026",   fr: "Pâques 2026" }, dates: "28 Mar — 04 Abr",
      tripRouteId: "tourFinDeAno2025",
      title:  { es: "Sáhara & Alto Atlas", en: "Sahara & High Atlas", fr: "Sahara & Haut Atlas" },
      summary: { es: "Del paisaje de montaña del Alto Atlas a las dunas del Sáhara: una salida para descubrir los grandes contrastes del sur de Marruecos.", en: "From High Atlas mountain scenery to Saharan dunes: a departure to discover the great contrasts of southern Morocco.", fr: "Des paysages de montagne du Haut Atlas aux dunes du Sahara : un départ pour découvrir les grands contrastes du sud marocain." },
      spots: 4, accent: "#C16542", image: "https://images.unsplash.com/photo-1542401886-65d6c61db217?auto=format&fit=crop&w=1600&q=85" },
    { season: { es: "Verano 2026", en: "Summer 2026", fr: "Été 2026" }, dates: "12 Jul — 23 Jul",
      title:  { es: "Norte de Marruecos & costas", en: "Northern Morocco & coasts", fr: "Nord du Maroc & côtes" },
      summary: { es: "Descubre el norte de Marruecos y sus costas, una propuesta para acercarte a sus ciudades y disfrutar de sus paisajes junto al mar.", en: "Discover northern Morocco and its coasts, a journey to explore its cities and enjoy its seaside landscapes.", fr: "Découvrez le nord du Maroc et ses côtes, un voyage pour explorer ses villes et profiter de ses paysages en bord de mer." },
      spots: 6, accent: "#3A4A5F", image: "https://images.unsplash.com/photo-1547234935-80c7145ec969?auto=format&fit=crop&w=1600&q=85" },
    { season: { es: "Otoño 2026", en: "Autumn 2026", fr: "Automne 2026" }, dates: "10 Oct — 21 Oct",
      title:  { es: "Marruecos al completo", en: "Full Morocco", fr: "Maroc intégral" },
      summary: { es: "Una propuesta para descubrir la diversidad de Marruecos: paisajes, ciudades y culturas que muestran las distintas caras del país en un mismo viaje.", en: "A journey through Morocco's diversity: landscapes, cities and cultures revealing the country's many faces in a single trip.", fr: "Un voyage à travers la diversité du Maroc : paysages, villes et cultures pour découvrir les multiples facettes du pays." },
      spots: 8, accent: "#A07042", image: "https://images.unsplash.com/photo-1570133435536-7ececf000ef6?auto=format&fit=crop&w=1600&q=85" },
    { season: { es: "Fin de año 2026", en: "New Year 2026", fr: "Nouvel An 2026" }, dates: "27 Dic — 03 Ene",
      title:  { es: "Bivouac Erg Chigaga", en: "Erg Chigaga bivouac", fr: "Bivouac Erg Chigaga" },
      summary: { es: "Despide el año entre las dunas de Erg Chigaga y vive la experiencia de un bivouac en el desierto: una forma diferente de recibir el nuevo año.", en: "See out the year among the dunes of Erg Chigaga and experience a desert bivouac: a different way to welcome the new year.", fr: "Terminez l’année parmi les dunes de l’Erg Chigaga et vivez l’expérience d’un bivouac au désert : une autre façon d’accueillir le nouvel an." },
      spots: 2, accent: "#D97742", image: "https://images.unsplash.com/photo-1547234935-80c7145ec969?auto=format&fit=crop&w=1600&q=85" },
  ];

  return (
    <section id="proximas" data-testid="viajes-proximas"
             className="relative bg-[#FDFBF7] py-24 md:py-32 overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-14">
          <div className="md:col-span-7">
            <span className="overline">{t.overline}</span>
            <h2 className="font-serif-x text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-5 text-[#2C2621]">
              {t.title}
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-base md:text-lg text-[#5C5248] leading-relaxed">{t.body}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 items-start sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {DEPARTURES.map((d, i) => (
            <ExpandableTripCard key={d.id || `proxima-${i}`} title={pick(d.title, lang)} lang={lang}
              description={pick(d.summary, lang)} href={pathFor(lang, d.tripRouteId || "contact")} ctaLabel={d.tripRouteId ? t.view_trip : t.reserve}
              testIdPrefix={`proxima-${i}`} testIds={{ card: `proxima-card-${i}` }} className="!bg-[#F2EBE1]">
              <div data-trip-card-image="" className="relative aspect-[5/4] overflow-hidden bg-[#1A1513]">
                <EditableImage
                  slot={`viajes.proxima.${d.id || i}`}
                  fallback={d.image}
                  alt={pick(d.title, lang)}
                  aspectRatio="5/4"
                  imgProps={{ loading: "lazy" }}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
                />
                <div
                  data-testid={`proxima-labels-${i}`}
                  className="absolute inset-x-4 top-4 z-[4] flex flex-wrap items-start gap-2"
                >
                  <span
                    data-testid={`proxima-season-${i}`}
                    className="inline-flex max-w-full items-center gap-2 bg-[#FDFBF7]/95 backdrop-blur-sm px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase"
                    style={{ color: d.accent }}
                  >
                    <Calendar className="w-3 h-3 shrink-0" strokeWidth={1.6} />
                    {pick(d.season, lang)}
                  </span>
                  {d.spots <= 2 && (
                    <span
                      data-testid={`proxima-last-spots-${i}`}
                      className="inline-flex max-w-full bg-[#C16542] text-[#FDFBF7] px-3 py-1.5 text-[9px] tracking-[0.25em] uppercase"
                    >
                      {t.last_spots}
                    </span>
                  )}
                </div>
                <XalucaLogoBadge
                  className="bottom-3 left-3 w-11 h-11"
                  testid={`proxima-logo-${i}`}
                />
              </div>
              <div className="p-4 sm:p-5 xl:p-6 flex min-w-0 flex-col flex-1">
                <p className="font-serif-x-italic text-base text-[#5C5248]">{d.dates}</p>
                <h3 className="break-words font-serif-x text-xl md:text-[22px] leading-[1.15] mt-2 text-[#2C2621]">
                  {pick(d.title, lang)}
                </h3>
                <div className="mt-3">
                  <FromPrice tone="dark" size="sm" className="flex-wrap" testid={`proxima-from-${i}`} />
                </div>
                <div className="mt-auto pt-5 border-t border-[#2C2621]/10 flex flex-wrap items-center justify-between gap-3">
                  <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">
                    {d.spots} {t.spots}
                  </span>
                  <Link to={pathFor(lang, "contact")} data-testid={`proxima-cta-${i}`}
                        className="xaluca-button inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#C16542] hover:gap-3 transition-all duration-300">
                    {t.reserve}
                    <ArrowRight className="w-3 h-3 shrink-0" strokeWidth={1.5} />
                  </Link>
                </div>
                {d.tripRouteId && (
                  <Link to={pathFor(lang, d.tripRouteId)} data-testid={`proxima-trip-cta-${i}`}
                        className="xaluca-button mt-4 inline-flex min-h-11 max-w-full items-center justify-center gap-2 border border-[#C16542]/40 px-4 py-3 text-center text-[10px] tracking-[0.25em] uppercase text-[#C16542] hover:bg-[#C16542] hover:text-white transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C16542] focus-visible:ring-offset-2">
                    {t.view_trip}
                    <ArrowRight className="w-3 h-3 shrink-0" strokeWidth={1.5} />
                  </Link>
                )}
              </div>
            </ExpandableTripCard>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   7 — Asesoramiento
============================================================ */
const Asesoramiento = ({ t, lang }) => {
  const OPTIONS = [
    { icon: Phone,         k: "phone",   accent: "#C16542", href: `tel:${CONTACT.phoneRaw}` },
    { icon: Calendar,      k: "visit",   accent: "#D4A373", href: pathFor(lang, "appointment") },
    { icon: MessageCircle, k: "spec",    accent: "#3A4A5F", href: pathFor(lang, "contact") },
  ];

  return (
    <section id="asesoramiento" data-testid="viajes-asesoramiento"
             className="relative bg-[#1A1513] text-[#FDFBF7] py-24 md:py-32 overflow-hidden">
      <EditableImage
        slot="viajes.asesoramiento.bg"
        fallback="https://images.unsplash.com/photo-1489493585363-d69421e0edd3?auto=format&fit=crop&w=2000&q=85"
        alt=""
        aspectRatio="21/9"
        imgProps={{ loading: "lazy" }}
        className="absolute inset-0 w-full h-full object-cover opacity-25"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#1A1513]/90 via-[#1A1513]/75 to-[#1A1513]/95 pointer-events-none" />
      <div className="absolute inset-0 berber-bg-cross opacity-40" aria-hidden="true" />
      <span className="film-grain" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl text-center mx-auto">
          <span className="overline text-[#D4A373]">{t.overline}</span>
          <h2 className="font-serif-x text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-5">
            {t.title}
          </h2>
          <p className="mt-5 font-serif-x-italic text-xl md:text-2xl text-[#D4A373]/90">{t.subtitle}</p>
          <p className="mt-6 text-base md:text-lg text-[#FDFBF7]/75 leading-relaxed">{t.body}</p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {OPTIONS.map((o, i) => {
            const Icon = o.icon;
            const isExternal = o.href.startsWith("tel:") || o.href.startsWith("mailto:") || o.href.startsWith("http");
            const Wrapper = isExternal ? "a" : Link;
            const props = isExternal ? { href: o.href } : { to: o.href };
            return (
              <Wrapper key={o.k} {...props} data-testid={`asesoramiento-card-${i}`}
                       className="group relative bg-[#FDFBF7]/[0.05] backdrop-blur-md border border-[#FDFBF7]/15 hover:border-[#D4A373]/50 transition-all duration-500 p-8 md:p-10 flex flex-col overflow-hidden">
                <div className="absolute -top-3 -right-3 berber-bg-cross w-24 h-24 opacity-25" aria-hidden="true" />
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full border"
                      style={{ borderColor: `${o.accent}88`, color: o.accent }}>
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </span>
                <h3 className="font-serif-x text-2xl md:text-[26px] leading-[1.1] mt-8 text-[#FDFBF7]">
                  {t[`${o.k}_t`]}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-[#FDFBF7]/70 flex-1">{t[`${o.k}_b`]}</p>
                <span className="mt-8 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase border-b pb-1 self-start group-hover:gap-3 transition-all duration-300"
                      style={{ borderColor: `${o.accent}66`, color: o.accent }}>
                  {t[`${o.k}_cta`]}
                  <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                </span>
              </Wrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   8 — Contact intro
============================================================ */
const ContactIntro = ({ t, lang }) => (
  <section data-testid="viajes-contact-intro" className="relative bg-[#FDFBF7] py-24 md:py-32 overflow-hidden">
    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="md:col-span-7">
          <span className="overline">{t.overline}</span>
          <h2 className="font-serif-x text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-5 text-[#2C2621]">
            {t.title}
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#5C5248] leading-relaxed max-w-2xl">{t.body}</p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to={pathFor(lang, "contact")} data-testid="viajes-contact-cta-form"
               className="xaluca-button inline-flex items-center gap-3 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-7 py-4 text-[11px] tracking-[0.25em] uppercase transition-colors">
              {t.cta_form}<ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
            </Link>
            <button type="button" onClick={() => requestWhatsAppContact(WHATSAPP_URL)}
               data-testid="viajes-contact-cta-wa"
               className="xaluca-button inline-flex items-center gap-3 border border-[#2C2621]/25 hover:border-[#2C2621] hover:bg-[#2C2621] hover:text-[#FDFBF7] text-[#2C2621] px-7 py-4 text-[11px] tracking-[0.25em] uppercase transition-all duration-300">
              <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.6} />WhatsApp
            </button>
          </div>
        </div>
        <div className="md:col-span-5 space-y-4">
          {[
            { label: t.phone_label, icon: Phone, value: CONTACT.phone, href: `tel:${CONTACT.phoneRaw}` },
            { label: t.email_label, icon: Mail,  value: CONTACT.email, href: `mailto:${CONTACT.email}` },
            { label: t.hours_label, icon: Building2, value: t.hours_value },
          ].map((c, i) => (
            <div key={c.label || `contact-${i}`} className="bg-[#F2EBE1] border border-[#2C2621]/10 p-6 md:p-7">
              <span className="overline">{c.label}</span>
              {c.href ? (
                <a href={c.href} className="mt-3 flex items-center gap-3 font-serif-x text-xl text-[#2C2621] hover:text-[#C16542] transition-colors break-all">
                  <c.icon className="w-5 h-5 text-[#C16542]" strokeWidth={1.5} />{c.value}
                </a>
              ) : (
                <p className="mt-3 flex items-center gap-3 text-sm text-[#2C2621]">
                  <c.icon className="w-4 h-4 text-[#C16542]" strokeWidth={1.5} />{c.value}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================
   Trilingual copy
============================================================ */
const COPY = {
  es: {
    docTitle: "Viajes por Marruecos · Xaluca Tours",
    hero: {
      eyebrow: "Xaluca Tours · Viajes", eyebrow_place: "Erg Chebbi · Sáhara",
      title: "Viajes por Marruecos",
      subtitle: "Descubre rutas únicas, escapadas exclusivas y aventuras inolvidables por el corazón de Marruecos.",
      intro: "Explora todos nuestros viajes por región o por estilo de experiencia. Circuitos organizados, salidas en grupo y viajes a medida durante todo el año.",
      cta_primary: "Explorar por región", cta_secondary: "Buscar mi viaje", scroll: "Desplázate",
    },
    intro: {
      eyebrow: "Una aventura, un país",
      title: "Marruecos, explorado a tu manera.",
      body: "Tres regiones, cinco estilos de experiencia y decenas de itinerarios — para que encuentres exactamente el viaje que estás imaginando.",
    },
    postcard: {
      label: "Postal desde Marruecos",
      greeting: "Querido viajero,",
      body: [
        "Hay lugares que parecen estar al otro lado del mundo y, sin embargo, están sorprendentemente cerca. Marruecos es uno de ellos. A pocas horas de casa, te esperan medinas llenas de vida, montañas infinitas, oasis escondidos y noches bajo las estrellas del desierto.",
        "Quizás por eso muchos lo llaman el país más cercano de los viajes lejanos. Porque aquí todo cambia: los colores, los aromas, los paisajes y la forma de vivir cada día. Y, al mismo tiempo, todo resulta accesible, auténtico y acogedor.",
      ],
      closing: "Nos vemos en el camino.",
      signature: "Xaluca Tours",
      tagline: "El viaje lejano más cercano",
      postmark: "MARRUECOS",
      stamp_caption: "Sáhara · Marruecos",
    },
    regions: {
      overline: "Por región", title: "Tres maneras de entrar en Marruecos.",
      body: "Cada región tiene su carácter, su luz y su ritmo. Elige por dónde quieres empezar — o recorre el país entero.",
      cta: "Descubrir rutas",
    },
    experiences: {
      overline: "Por experiencia", title: "Seis estilos de viaje.",
      body: "Del silencio del desierto a las cumbres del Atlas, de los talleres de artesanos a las salidas en grupo. Encuentra tu manera de viajar.",
      cta: "Ver viajes",
      cta_call: "Llámanos",
    },
    explorer: {
      overline: "Encuentra tu viaje", title: "Filtra y descubre.",
      body: "Combina región y experiencia para ver exactamente los viajes que encajan contigo.",
      label_region: "Región", label_experience: "Experiencia", all: "Todos",
      result_singular: "viaje encontrado", result_plural: "viajes encontrados", clear: "Limpiar filtros",
      empty_title: "No hay viajes con estos filtros.",
      empty_body: "Prueba otra combinación o solicita un viaje a medida — lo diseñaremos exactamente como lo imaginas.",
      from_label: "Desde", cta_card: "Más info",
      fav: "Guardar en favoritos", fav_remove: "Quitar de favoritos",
    },
    proximas: {
      overline: "Próximas salidas", title: "Próximas salidas a Marruecos.",
      body: "Circuitos organizados con fechas cerradas y salidas en grupo durante Semana Santa, verano, fin de año y otras ocasiones especiales.",
      last_spots: "Últimas plazas", spots: "plazas", reserve: "Reservar", view_trip: "Ver viaje",
    },
    asesoramiento: {
      overline: "Asesoramiento personalizado",
      title: "¿Tienes preguntas sobre tu viaje?",
      subtitle: "Asesoramiento online en tiempo real o visítanos en nuestras oficinas.",
      body: "Planifica tu próxima aventura por Marruecos y resuelve todas tus dudas con nuestros especialistas.",
      phone_t: "Reservar cita telefónica", phone_b: "Habla con un especialista por teléfono.", phone_cta: "Llamar ahora",
      visit_t: "Pedir cita previa", visit_b: "Acércate a nuestras oficinas y planifica tu viaje en persona.", visit_cta: "Pedir cita",
      spec_t: "Contactar con un especialista", spec_b: "Asesoramiento online en tiempo real con nuestro equipo.", spec_cta: "Escribir ahora",
    },
    contact: {
      overline: "Contacto", title: "¿Te interesan nuestros viajes por Marruecos?",
      body: "Sin compromiso, rellena el formulario o solicita una cita previa y nuestro equipo te ayudará a organizar tu próxima aventura.",
      cta_form: "Rellenar formulario",
      phone_label: "Teléfono", email_label: "Email", hours_label: "Horario",
      hours_value: "De lunes a viernes · 10h – 20h",
    },
  },
  en: {
    docTitle: "Morocco journeys · Xaluca Tours",
    hero: {
      eyebrow: "Xaluca Tours · Journeys", eyebrow_place: "Erg Chebbi · Sahara",
      title: "Morocco journeys",
      subtitle: "Discover unique routes, exclusive escapes and unforgettable adventures across the heart of Morocco.",
      intro: "Explore every trip by region or by travel style. Curated departures, group trips and tailor-made journeys all year round.",
      cta_primary: "Browse by region", cta_secondary: "Find my trip", scroll: "Scroll",
    },
    intro: {
      eyebrow: "An adventure, a country",
      title: "Morocco, explored your way.",
      body: "Three regions, five experience styles and dozens of itineraries — so you find exactly the journey you're imagining.",
    },
    postcard: {
      label: "A postcard from Morocco",
      greeting: "Dear traveller,",
      body: [
        "Some places seem to be on the other side of the world and yet they are surprisingly close. Morocco is one of them. Just a few hours from home, living medinas, endless mountains, hidden oases and nights under the desert stars await you.",
        "Maybe that's why many call it the nearest of faraway journeys. Because here everything changes: the colours, the scents, the landscapes and the way each day is lived. And, at the same time, it all feels accessible, authentic and welcoming.",
      ],
      closing: "See you on the road.",
      signature: "Xaluca Tours",
      tagline: "The nearest faraway journey",
      postmark: "MOROCCO",
      stamp_caption: "Sahara · Morocco",
    },
    regions: {
      overline: "By region", title: "Three ways into Morocco.",
      body: "Each region has its own character, light and rhythm. Choose where to start — or cross the whole country.",
      cta: "Discover routes",
    },
    experiences: {
      overline: "By experience", title: "Six travel styles.",
      body: "From desert silence to Atlas summits, from artisan workshops to group departures. Find the way you love to travel.",
      cta: "View trips",
      cta_call: "Call us",
    },
    explorer: {
      overline: "Find your trip", title: "Filter and discover.",
      body: "Combine region and experience to see exactly the trips that fit you.",
      label_region: "Region", label_experience: "Experience", all: "All",
      result_singular: "trip found", result_plural: "trips found", clear: "Clear filters",
      empty_title: "No trips with these filters.",
      empty_body: "Try another combination — or request a tailor-made trip and we'll design exactly what you imagine.",
      from_label: "From", cta_card: "Learn more",
      fav: "Save to favourites", fav_remove: "Remove from favourites",
    },
    proximas: {
      overline: "Upcoming departures", title: "Upcoming Morocco departures.",
      body: "Curated group departures with fixed dates throughout Easter, summer, New Year and other special occasions.",
      last_spots: "Last spots", spots: "spots", reserve: "Book", view_trip: "View trip",
    },
    asesoramiento: {
      overline: "Personal consultation",
      title: "Questions about your trip?",
      subtitle: "Real-time online advice or visit us at our offices.",
      body: "Plan your next Moroccan adventure and clear every doubt with our specialists.",
      phone_t: "Book a phone call", phone_b: "Speak with a specialist by phone.", phone_cta: "Call now",
      visit_t: "Book an in-person visit", visit_b: "Come to our offices and plan in person.", visit_cta: "Book a visit",
      spec_t: "Talk to a specialist", spec_b: "Real-time online advice from our expert team.", spec_cta: "Write now",
    },
    contact: {
      overline: "Contact", title: "Interested in our Morocco journeys?",
      body: "No commitment — fill out the form or request an appointment, and our team will help plan your next adventure.",
      cta_form: "Open the form",
      phone_label: "Phone", email_label: "Email", hours_label: "Hours",
      hours_value: "Monday — Friday · 10:00 – 20:00",
    },
  },
  fr: {
    docTitle: "Voyages au Maroc · Xaluca Tours",
    hero: {
      eyebrow: "Xaluca Tours · Voyages", eyebrow_place: "Erg Chebbi · Sahara",
      title: "Voyages au Maroc",
      subtitle: "Découvrez des itinéraires uniques, des escapades exclusives et des aventures inoubliables au cœur du Maroc.",
      intro: "Parcourez tous nos voyages par région ou par style. Départs organisés, groupes et voyages sur mesure toute l'année.",
      cta_primary: "Par région", cta_secondary: "Trouver mon voyage", scroll: "Faites défiler",
    },
    intro: {
      eyebrow: "Une aventure, un pays",
      title: "Le Maroc, à votre façon.",
      body: "Trois régions, cinq styles d'expérience et des dizaines d'itinéraires — pour trouver exactement le voyage que vous imaginez.",
    },
    postcard: {
      label: "Une carte postale du Maroc",
      greeting: "Cher voyageur,",
      body: [
        "Certains lieux semblent être à l'autre bout du monde et pourtant ils sont étonnamment proches. Le Maroc est l'un d'eux. À quelques heures de chez vous vous attendent des médinas pleines de vie, des montagnes infinies, des oasis cachées et des nuits sous les étoiles du désert.",
        "C'est peut-être pour cela que beaucoup l'appellent le plus proche des voyages lointains. Parce qu'ici tout change : les couleurs, les parfums, les paysages et la façon de vivre chaque jour. Et, en même temps, tout y est accessible, authentique et chaleureux.",
      ],
      closing: "On se retrouve sur la route.",
      signature: "Xaluca Tours",
      tagline: "Le voyage lointain le plus proche",
      postmark: "MAROC",
      stamp_caption: "Sahara · Maroc",
    },
    regions: {
      overline: "Par région", title: "Trois portes d'entrée au Maroc.",
      body: "Chaque région a son caractère, sa lumière et son rythme. Choisissez où commencer — ou traversez tout le pays.",
      cta: "Découvrir les itinéraires",
    },
    experiences: {
      overline: "Par expérience", title: "Six styles de voyage.",
      body: "Du silence du désert aux sommets de l'Atlas, des ateliers d'artisans aux départs en groupe. Trouvez votre façon de voyager.",
      cta: "Voir les voyages",
      cta_call: "Appelez-nous",
    },
    explorer: {
      overline: "Trouvez votre voyage", title: "Filtrez et découvrez.",
      body: "Combinez région et expérience pour voir exactement les voyages qui vous correspondent.",
      label_region: "Région", label_experience: "Expérience", all: "Tous",
      result_singular: "voyage trouvé", result_plural: "voyages trouvés", clear: "Effacer les filtres",
      empty_title: "Aucun voyage avec ces filtres.",
      empty_body: "Essayez une autre combinaison ou demandez un voyage sur mesure — nous le concevrons exactement comme vous l'imaginez.",
      from_label: "Dès", cta_card: "En savoir plus",
      fav: "Enregistrer dans mes favoris", fav_remove: "Retirer des favoris",
    },
    proximas: {
      overline: "Prochains départs", title: "Prochains départs au Maroc.",
      body: "Départs en groupe à dates fixes pendant Pâques, l'été, le Nouvel An et d'autres occasions spéciales.",
      last_spots: "Dernières places", spots: "places", reserve: "Réserver", view_trip: "Voir le voyage",
    },
    asesoramiento: {
      overline: "Conseil personnalisé",
      title: "Des questions sur votre voyage ?",
      subtitle: "Conseil en ligne en temps réel ou rendez-vous à nos bureaux.",
      body: "Planifiez votre prochaine aventure au Maroc et levez tous vos doutes avec nos spécialistes.",
      phone_t: "Réserver un appel", phone_b: "Parlez à un spécialiste au téléphone.", phone_cta: "Appeler",
      visit_t: "Prendre rendez-vous", visit_b: "Venez à nos bureaux et planifiez en personne.", visit_cta: "Prendre rendez-vous",
      spec_t: "Contacter un spécialiste", spec_b: "Conseil en ligne en temps réel.", spec_cta: "Écrire",
    },
    contact: {
      overline: "Contact", title: "Nos voyages au Maroc vous intéressent ?",
      body: "Sans engagement — remplissez le formulaire ou demandez un rendez-vous, notre équipe organisera votre prochaine aventure.",
      cta_form: "Ouvrir le formulaire",
      phone_label: "Téléphone", email_label: "Email", hours_label: "Horaires",
      hours_value: "Lundi — Vendredi · 10h – 20h",
    },
  },
};

/* ============================================================
   Page
============================================================ */
/* ============================================================
   Catalogo CTA — direct shortcut to the Publuu flipbook page.
   Editorial dark band with eyebrow + headline + body + a
   primary CTA pointing to /catalogo. Sits between Asesoramiento
   and the contact form so it lands in the user's flow before
   reading the form, encouraging exploration of the printed
   catalogue alongside the interactive listing they just browsed.
============================================================ */
const CATALOGO_CTA_COPY = {
  es: {
    eyebrow: "Catálogo digital",
    title:   "Hojea todos nuestros catálogos en un solo lugar",
    body:    "Descubre rutas, escapadas y experiencias en formato revista — itinerarios día a día, mapas y fotografía editorial — listos para inspirar tu próxima aventura por Marruecos.",
    cta:     "Abrir el catálogo",
    note:    "Formato flipbook · Lectura desde cualquier dispositivo",
  },
  en: {
    eyebrow: "Digital catalogue",
    title:   "Browse every catalogue in one place",
    body:    "Discover routes, short escapes and experiences in a magazine-style flipbook — day-by-day itineraries, maps and editorial photography — ready to inspire your next Moroccan adventure.",
    cta:     "Open the catalogue",
    note:    "Flipbook format · Reads on any device",
  },
  fr: {
    eyebrow: "Catalogue numérique",
    title:   "Feuilletez tous nos catalogues au même endroit",
    body:    "Découvrez nos routes, escapades et expériences en format magazine — itinéraires jour par jour, cartes et photographie éditoriale — prêts à inspirer votre prochaine aventure marocaine.",
    cta:     "Ouvrir le catalogue",
    note:    "Format flipbook · Lecture sur tout appareil",
  },
};

const CatalogoCTA = ({ lang }) => {
  const c = CATALOGO_CTA_COPY[lang] || CATALOGO_CTA_COPY.es;
  return (
    <section
      data-testid="tours-landing-catalogo-cta"
      className="relative w-full py-20 md:py-28 bg-[#1A1513] overflow-hidden"
    >
      <div className="absolute inset-0 berber-bg-cross opacity-25 pointer-events-none" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-br from-[#1A1513] via-[#2C2621] to-[#1A1513] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="lg:col-span-7">
            <span className="block text-[11px] tracking-[0.4em] uppercase text-[#D4A373] mb-5">
              {c.eyebrow}
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#FDFBF7] leading-tight tracking-tight">
              {c.title}
            </h2>
            <p className="mt-6 text-[14px] md:text-base text-[#FDFBF7]/80 leading-relaxed max-w-2xl">
              {c.body}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <Link
                to={pathFor(lang, "catalog")}
                data-testid="tours-landing-catalogo-cta-link"
                className="xaluca-button inline-flex items-center gap-3 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-8 py-4 text-[11px] tracking-[0.28em] uppercase transition-colors"
              >
                {c.cta}
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.7} />
              </Link>
              <span className="text-[10px] tracking-[0.28em] uppercase text-[#D4A373]/80">
                {c.note}
              </span>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[3/4] overflow-hidden border border-[#D4A373]/25 shadow-[0_30px_70px_-30px_rgba(0,0,0,0.6)]">
              <EditableImage
                slot="tours-landing.catalogo-cta.cover"
                fallback="https://images.unsplash.com/photo-1565689157206-0fddef7589a2?auto=format&fit=crop&w=900&q=85"
                alt={c.title}
                aspectRatio="3/4"
                imgProps={{ loading: "lazy" }}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <span className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#1A1513] to-transparent pointer-events-none" />
              <span className="absolute bottom-4 left-4 text-[9px] tracking-[0.32em] uppercase text-[#FDFBF7]/85">
                Xaluca Tours · Catálogo 2026
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function ToursLandingPage() {
  const { lang } = useLanguage();
  const t = COPY[lang] || COPY.es;

  useEffect(() => {
    document.title = t.docTitle;
    window.scrollTo(0, 0);
  }, [t.docTitle]);

  return (
    <div data-testid="tours-landing-page">
      <Hero t={t.hero} />
      <SectionNav
        testid="viajes-nav"
        items={[
          { id: "destacados", label: { es: "Destacados", en: "Featured", fr: "À la une" } },
          { id: "intro", label: { es: "Introducción", en: "Overview", fr: "Introduction" } },
          { id: "videos-marruecos", label: { es: "Vídeos", en: "Videos", fr: "Vidéos" } },
          { id: "regions", label: { es: "Regiones", en: "Regions", fr: "Régions" } },
          { id: "experiences", label: { es: "Experiencias", en: "Experiences", fr: "Expériences" } },
          { id: "ideal-trip", label: { es: "Tu viaje ideal", en: "Your ideal trip", fr: "Votre voyage idéal" } },
          { id: "explorer", label: { es: "Explorador", en: "Explorer", fr: "Explorateur" } },
          { id: "proximas", label: { es: "Próximas salidas", en: "Upcoming", fr: "Prochains départs" } },
          { id: "asesoramiento", label: { es: "Asesoramiento", en: "Advice", fr: "Conseil" } },
        ]}
      />
      <FeaturedDepartureBanner context="tours" />
      <EditorialIntro t={t.intro} postcard={t.postcard} />
      <ToursRegionMap />
      <PexelsMoroccoVideos />
      <RegionsSection t={t.regions} lang={lang} />
      <ExperiencesSection t={t.experiences} lang={lang} />
      <IdealTripWizard />
      <TripExplorer t={t.explorer} lang={lang} />
      <HomeAllTripsCatalog initialLimit={12} expandableCards />
      <ProximasSalidas t={t.proximas} lang={lang} />
      <Asesoramiento t={t.asesoramiento} lang={lang} />
      <CatalogoCTA lang={lang} />
      <ContactIntro t={t.contact} lang={lang} />
      <ContactForm />
    </div>
  );
}
